/**
 * Top-level component for the digital SAT landing page.
 */
import "../../../stylesheets/shared-package/roboto.less";

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect} from "react";
import {useHistory} from "react-router-dom";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {
    MEDIA_DEFAULT_SPEC,
    MediaLayoutContext,
} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useMarkConversion} from "@khan/analytics-learning-time";
import {TRANSFER_AUTH_TOKEN_MUTATION} from "@khan/auth-shared";
import {useMutation} from "@khan/data-gql";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl, getTransferAuthUrl, parseUrl, urlInLanguage} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";

import Divider from "../components/divider.tsx";
import CoachModule from "../components/modules/coach-module.tsx";
import CtaModule from "../components/modules/cta-module.tsx";
import HeroImage from "../components/modules/hero-image.tsx";
import InsideScoopModule from "../components/modules/inside-scoop-module.tsx";
import PracticeModule from "../components/modules/practice-module.tsx";
import QuoteModule from "../components/modules/quote-module.tsx";
import SummaryModule from "../components/modules/summary-module.tsx";
import * as urls from "../lib/urls.ts";

function useTargetUrl(): string | null | undefined {
    const [createTransferAuthToken, result] = useMutation(
        TRANSFER_AUTH_TOKEN_MUTATION,
    );

    if (RequestInfo.kaLocale === "en") {
        return urls.digitalSatTarget;
    }

    const loggedOutTarget = urlInLanguage(
        "en",
        parseUrl(urls.digitalSatTarget),
    );
    if (!PermissionsInfo.isLoggedIn) {
        return loggedOutTarget;
    }

    if (!result) {
        // This calls the mutation, which will cause a re-render.
        createTransferAuthToken();
        return null;
    }

    if (result.status === "loading" || result.status === "no-data") {
        return null; // will re-render when the status changes again
    }

    const transferAuthToken =
        result.status === "success"
            ? result.data.createTransferAuthToken?.token
            : null;
    if (!transferAuthToken) {
        return loggedOutTarget;
    }

    return getTransferAuthUrl(transferAuthToken, urls.digitalSatTarget, "en");
}

type Props = {
    confirmed: "confirmed" | null | undefined;
};

export default function DigitalSatLandingPage({
    confirmed,
}: Props): React.ReactElement {
    const targetUrl = useTargetUrl();
    const markConversion = useMarkConversion();

    const history = useHistory();

    const isConfirmed = confirmed === "confirmed";

    // Remove /confirmed from the end of the URL.
    // This used to tell us whether the user had confirmed which
    // version of the SAT they were planning to take. Now that we only
    // have digital SAT prep, this isn't relevant, so we redirect to
    // reduce clutter in the URL.
    useEffect(() => {
        if (isConfirmed) {
            history.replace(urls.digitalSatLandingPage({confirmed: false}));
        }
    }, [isConfirmed, history]);

    useOnMountEffect(() => {
        markConversion({id: "sat_pageview_landing_page"});
    });

    const {isPhone, isTablet} = RequestInfo;

    return (
        <MediaLayoutContext.Provider
            value={{
                ssrSize: isPhone ? "small" : isTablet ? "medium" : "large",
                mediaSpec: MEDIA_DEFAULT_SPEC,
            }}
        >
            <PageRoot
                settings={{
                    title: t`Official Digital SAT® Prep`,
                }}
            >
                <View style={styles.heroBackground}>
                    <View style={styles.heroBackgroundIllustration}>
                        <HeroImage
                            signupUrl={targetUrl}
                            title={
                                <Trans>
                                    Maximize your score with free Official
                                    Digital SAT
                                    <sup className={css(styles.superscript)}>
                                        ®
                                    </sup>{" "}
                                    Prep
                                </Trans>
                            }
                        />
                    </View>
                </View>
                <SummaryModule
                    features={[
                        {
                            key: "official",
                            image: "/images/dsat-landing/feature-icon-official.svg",
                            imageWidth: "130",
                            title: t`Official`,
                            body: t`We partnered directly with College Board, the creators of the digital SAT, to help you focus on the exact skills you need to succeed on the test.`,
                        },
                        {
                            key: "interactive",
                            image: "/images/dsat-landing/feature-icon-interactive.svg",
                            imageWidth: "130",
                            title: t`Interactive`,
                            body: t`Sharpen your skills with our library of thousands of practice questions, videos, lessons, and hints plus test-taking tips and strategies.`,
                        },
                        {
                            key: "high-quality",
                            image: "/images/dsat-landing/feature-icon-high-quality.svg",
                            imageWidth: "130",
                            title: t`High Quality`,
                            body: t`Prep for every section of the digital SAT with guidance from Khan Academy’s team of math, reading, and writing experts.`,
                        },
                        {
                            key: "free",
                            image: "/images/dsat-landing/feature-icon-free.svg",
                            imageWidth: "130",
                            title: t`Free`,
                            body: t`Access all of our digital SAT prep materials for free—anytime, anywhere.`,
                        },
                    ]}
                />
                <Divider />
                <PracticeModule
                    title={t`Get ready for test day by practicing with purpose`}
                    body={
                        <>
                            {t`Sharpen your skills, reduce stress, and feel confident on test day. Here’s how:`}
                            <ul className={css(styles.list)}>
                                <li className={css(styles.bulletPoint)}>
                                    {t`Take a full-length practice test on College Board’s site`}
                                </li>
                                <li className={css(styles.bulletPoint)}>
                                    {t`Learn and practice your skills on Khan Academy`}
                                </li>
                            </ul>
                        </>
                    }
                    imageUrl="/images/dsat-landing/laptop-practice.png"
                    imageAltText={t`A student diligently preparing for the digital SAT`}
                    maxImageWidth={370}
                />
                <Divider />
                <InsideScoopModule />
                <QuoteModule />
                <CoachModule
                    educatorDescription={t`Encourage your students to download Bluebook™ and get familiar with the digital SAT test content and platform.`}
                    educatorTargetUrl={urls.digitalSatCoachUrl}
                    parentDescription={t`SAT practice on Khan Academy is world-class SAT prep that’s effective and 100% free. Encourage your teen to get a head start and practice today!`}
                    parentTargetUrl={urls.digitalSatCoachUrl}
                />
                <View style={styles.ctaBackground}>
                    <CtaModule
                        targetUrl={targetUrl}
                        title={
                            <Trans>
                                The best way to practice for the digital SAT is
                                free for{" "}
                                <em className={css(styles.circledText)}>
                                    <View style={styles.circle} />
                                    everyone.
                                </em>
                            </Trans>
                        }
                    />
                </View>
            </PageRoot>
        </MediaLayoutContext.Provider>
    );
}

const styles = StyleSheet.create({
    heroBackground: {
        backgroundImage: cssUrl("/images/dsat-landing/hero-background.png"),
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        [mediaQueries.smOrSmaller]: {
            backgroundImage: cssUrl(
                "/images/rsat-landing/mobile_hero-background.jpg",
            ),
        },
    },
    heroBackgroundIllustration: {
        [mediaQueries.smOrSmaller]: {
            backgroundImage: cssUrl(
                "/images/rsat-landing/mobile_hero-illustration.gif",
            ),
            backgroundSize: "cover",
        },
    },
    list: {
        marginTop: spacing.xLarge_32,
        listStyle: "disc outside",
        marginLeft: spacing.medium_16,
    },
    bulletPoint: {
        marginLeft: spacing.xSmall_8,
    },
    ctaBackground: {
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundImage: cssUrl("/images/dsat-landing/cta-background.png"),
    },

    // Style for superscript because the <sup> tag doesn't always work.
    superscript: {
        fontSize: "35%",
        position: "relative",
        verticalAlign: "baseline",
        top: "-1.4em",
    },
    circle: {
        position: "absolute",
        zIndex: -1,
        backgroundImage: cssUrl("/images/rsat-landing/text-circle.svg"),
        backgroundSize: "100% 100%",
        right: "-1%",
        top: "-27%",
        height: "149%",
        width: "112%",
        [mediaQueries.smOrSmaller]: {
            right: "-16%",
            top: "-30%",
            height: "167%",
            width: "134%",
        },
    },
    circledText: {
        position: "relative",
        fontStyle: "normal",
    },
});
